import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material"
import {
  AddCircleOutlined,
  AddOutlined,
  EditOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import ProductAislesPreferenceFormDialog from "./ProductAislesPreferenceFormDialog"
import ProductAislesPreferencesTable from "./ProductAislesPreferencesTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import PermissionContentWrapper from "@/components/PermissionWrapper/PermissionContentWrapper"
import { PermissionActionType } from "@/constants/permissions"
import type { ProductAislesPreference } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface ProductAislesPreferenceSectionProps {
  productId: number
  aislesPreferences: ProductAislesPreference[]
  isFetching: boolean
  onRefresh: () => void
}

const ProductAislesPreferencesSection: FC<ProductAislesPreferenceSectionProps> = ({
  productId,
  aislesPreferences,
  isFetching,
  onRefresh,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isExpanded, setIsExpanded] = useState(true)
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingAislesPreference, setEditingAislesPreference] = useState<ProductAislesPreference | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { hasPermissions } = usePermissionManager()

  const handleDropdownOpen = () => setIsDropdownOpen(true)

  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleCreateDialogOpen = () => {
    setIsDropdownOpen(false)
    setEditingAislesPreference(null)
    setIsFormDialogOpen(true)
  }

  const handleEditDialogOpen = (aislesPreference: ProductAislesPreference) => {
    setEditingAislesPreference(aislesPreference)
    setIsFormDialogOpen(true)
  }

  const handleFormDialogSubmit = () => {
    setIsFormDialogOpen(false)
    setEditingAislesPreference(null)
    onRefresh()
  }

  const handleFormDialogClose = () => setIsFormDialogOpen(false)

  return (
    <Paper variant='outlined' elevation={0} className='border-gray-400 p-6' data-testid='product-aisle-preferences'>
      <Box className='flex items-center justify-between'>
        <Typography variant='subtitle1' component='h2' className='mr-auto text-ml'>
          Preferred Aisles
        </Typography>
        {aislesPreferences.length > 0 && hasPermissions([PermissionActionType.WAREHOUSES_MAINTENANCE]) && (
          <>
            <IconButton color='primary' onClick={handleDropdownOpen} ref={anchorElRef} aria-label='More'>
              <EditOutlined />
            </IconButton>
            <Menu
              anchorEl={anchorElRef.current}
              open={isDropdownOpen}
              onClose={handleDropdownClose}
              data-testid='header-dropdown-menu'
            >
              <MenuItem onClick={handleCreateDialogOpen}>
                <ListItemIcon>
                  <AddCircleOutlined />
                </ListItemIcon>
                <ListItemText primary='Add Warehouse' />
              </MenuItem>
            </Menu>
          </>
        )}
        <IconButton onClick={() => setIsExpanded(!isExpanded)} aria-label={isExpanded ? "Collapse" : "Expand"}>
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <Box className='mt-6'>
          {aislesPreferences.length === 0 && isFetching && (
            <Box className='flex w-full justify-center'>
              <CircularProgress />
            </Box>
          )}
          {aislesPreferences.length === 0 && !isFetching && (
            <PermissionContentWrapper permissions={[PermissionActionType.WAREHOUSES_MAINTENANCE]}>
              <InformationMissingAlert
                title='Add Preferred Aisles Information'
                action={
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    startIcon={<AddOutlined />}
                    onClick={handleCreateDialogOpen}
                  >
                    Preferred Aisles Information
                  </Button>
                }
              />
            </PermissionContentWrapper>
          )}
          {aislesPreferences.length > 0 && (
            <ProductAislesPreferencesTable aislesPreferences={aislesPreferences} onEdit={handleEditDialogOpen} />
          )}
          {isFormDialogOpen && (
            <ProductAislesPreferenceFormDialog
              productId={productId}
              aislesPreference={editingAislesPreference}
              onSubmit={handleFormDialogSubmit}
              onClose={handleFormDialogClose}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default ProductAislesPreferencesSection
